import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import { Button } from '../button'
import useDefer from '../../common/hooks/useDefer'
import Text from '../../ui/Text'
import { WidgetLayout } from '../widget-layout'
import { deletePartnerAdmin } from '../delete-partner-admin'
import { AddPartnerAdminModal } from '../add-partner-admin-modal'
import { CurrentUser } from '../currentUser'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../table'
import useText from '../../common/hooks/useText'

import BootstrapModalLayout from './../../ui/BootstrapModalLayout'

export type PartnerAdminsWidgetProps = {
    partnerUsers?: Array<CurrentUser>
    reload: () => void
    partnerId: string
    className?: ClassName
    style?: React.CSSProperties
}

export const SDangerButton = styled(Button)`
    &,
    &:not(:hover) {
        background-color: ${({ theme }) => theme.brandColors.CA_Coral};
        color: ${({ theme }) => theme.font.lightestColor};
        padding: 3px;
        border-radius: 3px;
    }

    &:hover {
        background-color: ${({ theme }) => theme.font.errorColor};
        color: ${({ theme }) => theme.font.lightestColor};
    }

    &:disabled,
    &:disabled:hover {
        background-color: ${({ theme }) => theme.checkbox.disabledMarkColor};
    }
`

export const PartnerAdminsWidget = React.memo(function PartnerAdminsWidget({
    partnerId,
    partnerUsers,
    reload,
    className,
    style,
}: PartnerAdminsWidgetProps): React.ReactElement {
    const text = useText()
    const [isPartnerAdminModalOpened, setIsPartnerAdminModalOpened] = useState(false)
    const { execute: remove } = useDefer((userId) => deletePartnerAdmin(partnerId, userId).then(reload), [partnerId])

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            headerButtons={
                <Button
                    title={text('btnAdd')}
                    onClick={() => setIsPartnerAdminModalOpened(true)}
                    small
                    dataTestId="addPartnerAdmin"
                />
            }
            title={text('lblPartnerAdmins')}
            data-testid={'partnerAdmins'}
        >
            {isPartnerAdminModalOpened && (
                <BootstrapModalLayout>
                    <AddPartnerAdminModal
                        adminPartners={partnerUsers || []}
                        partnerId={partnerId}
                        close={() => setIsPartnerAdminModalOpened(false)}
                        onLoadPartnerAdmin={reload}
                    />
                </BootstrapModalLayout>
            )}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell data-testid="theadName">
                            <Text code="lblName" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="theadEmail">
                            <Text code="lblEmail" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="theadRemove">
                            <Text code="lblRemove" />
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {partnerUsers?.map((user, i) => (
                        <TableRow key={Math.random()}>
                            <TableBodyCell data-testid={'userName-' + i}>
                                {user.firstName} {user.lastName}
                            </TableBodyCell>
                            <TableBodyCell data-testid={'userEmail-' + i}>{user.email}</TableBodyCell>
                            <TableBodyCell>
                                <SDangerButton
                                    tooltip={text('btnRemove')}
                                    onClick={() => remove(user.id)}
                                    title={<span className="fa fa-remove" />}
                                    data-testid={'userRemove-' + i}
                                    small
                                />
                            </TableBodyCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </WidgetLayout>
    )
})
